import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useUpdateGroup } from "@/hooks/use-data";
import { ApiError } from "@/lib/api";
import type {
  ConflictBody,
  GroupRecord,
  TargetEnv,
  UnifiedGroupRecord,
  UpdateGroupResponse,
} from "@/lib/types";
import { useMemo, useState } from "react";
import { TargetEnvRadio } from "./target-env-radio";

// IR-661 — modal Dialog for editing a group. `system_update_time` is the
// optimistic-concurrency token: the caller passes it through from the
// detail GET; absent → PUT goes without `X-If-Unmodified-Since`.

interface Props {
  group: UnifiedGroupRecord;
  /** Echoed back as `X-If-Unmodified-Since` for optimistic-concurrency
   *  protection. When the backend's row is newer the PUT returns 409 and we
   *  swap the body for a Reload prompt. */
  system_update_time?: string;
  onClose: () => void;
  /** IR-661 Phase 7 Task 14 (Q4=4a): when the user checks "Apply changes to
   *  existing members" and the backend kicks off async propagation
   *  (members ≥500 in either env), the response carries
   *  `_dual_env.propagate.{prod,dev}.{mode:'async', job_id}`. Callers
   *  should push these job_ids into the active-jobs banner store so the
   *  operator can watch them complete. Only async ids are reported (sync
   *  rows already have their `affected` count baked into the response). */
  onPropagateJobs?: (jobIds: { prod: string | null; dev: string | null }) => void;
}

interface FormState {
  group_name: string;
  classification: string;
  risk_score: string;
}

function initialForm(group: UnifiedGroupRecord): FormState {
  return {
    group_name: group.display_name ?? "",
    classification: group.classification ?? "",
    risk_score: group.risk_score == null ? "" : String(group.risk_score),
  };
}

function isDirty(form: FormState, group: UnifiedGroupRecord): boolean {
  if (form.group_name !== (group.display_name ?? "")) return true;
  if (form.classification !== (group.classification ?? "")) return true;
  const initialRisk = group.risk_score == null ? "" : String(group.risk_score);
  if (form.risk_score !== initialRisk) return true;
  return false;
}

function dirtyPayload(form: FormState, group: UnifiedGroupRecord): Partial<GroupRecord> {
  const out: Partial<GroupRecord> = {};
  if (form.group_name !== (group.display_name ?? "")) {
    out.group_name = form.group_name;
  }
  if (form.classification !== (group.classification ?? "")) {
    out.classification = form.classification;
  }
  const initialRisk = group.risk_score == null ? "" : String(group.risk_score);
  if (form.risk_score !== initialRisk) {
    out.risk_score = form.risk_score === "" ? null : Number(form.risk_score);
  }
  return out;
}

function divergedEnvFields(group: UnifiedGroupRecord): string[] {
  // Per-env signal currently available on UnifiedGroupRecord:
  // status only (members/messages drift is observability, not edit-state).
  const out: string[] = [];
  if (
    group.prod_status != null &&
    group.dev_status != null &&
    group.prod_status !== group.dev_status
  ) {
    out.push("status");
  }
  return out;
}

export function GroupEditDialog({ group, system_update_time, onClose, onPropagateJobs }: Props) {
  const [form, setForm] = useState<FormState>(() => initialForm(group));
  const [propagate, setPropagate] = useState(false);
  const [conflict, setConflict] = useState<ConflictBody | null>(null);
  // IR-661 Rotem reopen (2026-05-11): required env scope, no default.
  const [targetEnv, setTargetEnv] = useState<TargetEnv | null>(null);
  const mutation = useUpdateGroup();

  const dirty = isDirty(form, group);
  const diverged = useMemo(() => divergedEnvFields(group), [group]);
  const errorMessage =
    mutation.error && !(mutation.error instanceof ApiError && mutation.error.status === 409)
      ? (mutation.error.message ?? null)
      : null;

  function onSave() {
    if (targetEnv === null) return;
    const payload: Partial<GroupRecord> & { propagate_to_members?: boolean } = dirtyPayload(
      form,
      group,
    );
    if (propagate) {
      // Q4=4a: backend reads `propagate_to_members` and runs the
      // sync-vs-async propagator with a 500-member threshold per env.
      payload.propagate_to_members = true;
    }
    mutation.mutate(
      { id: group.id, data: payload, ifUnmodifiedSince: system_update_time, targetEnv },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            // 409 body shape per types.ts:786-792.
            setConflict(err.body as ConflictBody);
          }
        },
        onSuccess: (resp: UpdateGroupResponse) => {
          // Surface async propagate job_ids to the parent's active-jobs
          // banner store. Sync envs (mode='sync') don't produce a job
          // id — they're already applied by the time the response
          // returns. We only forward `mode='async'` ids.
          const prop = resp._dual_env?.propagate;
          if (!prop || !onPropagateJobs) return;
          const prodId =
            prop.prod && prop.prod.mode === "async" && prop.prod.job_id ? prop.prod.job_id : null;
          const devId =
            prop.dev && prop.dev.mode === "async" && prop.dev.job_id ? prop.dev.job_id : null;
          if (prodId || devId) onPropagateJobs({ prod: prodId, dev: devId });
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, padding: 20 }}>
        {conflict ? (
          <ConflictReloadBody conflict={conflict} onReload={onClose} />
        ) : (
          <>
            <div>
              <DialogTitle>Edit group</DialogTitle>
              <DialogDescription>
                {targetEnv === null
                  ? "Pick a target environment to enable Save."
                  : targetEnv === "both"
                    ? "Edits fan out to BOTH prod and dev. Per-env results return in the response envelope."
                    : `Edits apply to ${targetEnv === "prod" ? "production" : "development"} only.`}
              </DialogDescription>
            </div>

            <TargetEnvRadio value={targetEnv} onChange={setTargetEnv} actionVerb="edit" />

            {diverged.length > 0 ? (
              <div
                style={{
                  fontSize: 11,
                  color: "var(--iris-neon-yellow)",
                  border: "1px solid var(--iris-neon-yellow)",
                  borderRadius: 6,
                  padding: "6px 10px",
                  alignSelf: "flex-start",
                }}
                aria-label="differs across envs"
              >
                differs across envs ({diverged.join(", ")})
              </div>
            ) : null}

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label
                htmlFor="ir661-edit-group-name"
                style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
              >
                Group Name
                <input
                  id="ir661-edit-group-name"
                  className="iris-input"
                  value={form.group_name}
                  onChange={(e) => setForm((f) => ({ ...f, group_name: e.target.value }))}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </label>

              <label
                htmlFor="ir661-edit-group-classification"
                style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
              >
                Classification
                <input
                  id="ir661-edit-group-classification"
                  className="iris-input"
                  value={form.classification}
                  onChange={(e) => setForm((f) => ({ ...f, classification: e.target.value }))}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </label>

              <label
                htmlFor="ir661-edit-group-risk"
                style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
              >
                Risk Score
                <input
                  id="ir661-edit-group-risk"
                  type="number"
                  min={0}
                  max={100}
                  className="iris-input"
                  value={form.risk_score}
                  onChange={(e) => setForm((f) => ({ ...f, risk_score: e.target.value }))}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </label>

              {/* IR-661 Phase 7 Task 14 (Q4=4a): group-only propagate checkbox.
                  Entity sheet does NOT carry this control. */}
              <label
                htmlFor="ir661-edit-group-propagate"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  color: "var(--iris-fg-2)",
                  marginTop: 4,
                }}
              >
                <input
                  id="ir661-edit-group-propagate"
                  type="checkbox"
                  checked={propagate}
                  onChange={(e) => setPropagate(e.target.checked)}
                />
                Apply changes to existing members
              </label>
            </div>

            {errorMessage ? (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--iris-neon-pink)",
                  padding: "8px 10px",
                  border: "1px solid var(--iris-neon-pink)",
                  borderRadius: 6,
                }}
              >
                {errorMessage}
              </div>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: "auto",
              }}
            >
              <button
                type="button"
                className="iris-btn"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Discard
              </button>
              <button
                type="button"
                className="iris-btn iris-btn--primary"
                onClick={onSave}
                disabled={!dirty || mutation.isPending || targetEnv === null}
              >
                {mutation.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConflictReloadBody({
  conflict,
  onReload,
}: {
  conflict: ConflictBody;
  onReload: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <DialogTitle>Stale data</DialogTitle>
      <DialogDescription>
        {conflict.detail ||
          "The row was modified by someone else while you were editing. Reload and retry."}
      </DialogDescription>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="iris-btn iris-btn--primary" onClick={onReload}>
          Reload
        </button>
      </div>
    </div>
  );
}
